import { useState, useCallback } from 'react';

/**
 * Hook customizado para manejar el estado y eventos del formulario de evaluación
 * Centraliza toda la lógica de manejo de inputs, selects y checkboxes
 */
const useEvaluacionForm = (initialData = {}) => {
  const [formData, setFormData] = useState({
    nombreEvaluacion: '',
    tipoPrueba: '',
    subtipoPruebaPsicologica: '',
    idTipoCargo: '',
    cargosSeleccionados: [],
    encabezadoDescripcion: '',
    instrucciones: '',
    tiempoLimite: '',
    puntajeAprobatorio: 0,
    pesoCalificacion: '',
    estadoActivo: true,
    esObligatoria: true,
    intentosPermitidos: 2,
    preguntas: [],
    puntajeTotal: 0,
    ...initialData
  });

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Validación especial para tiempoLimite
    if (name === 'tiempoLimite') {
      if (value === '') {
        setFormData(prev => ({ ...prev, [name]: '' }));
        return;
      }
      // Solo dígitos enteros, sin punto ni coma
      if (!/^\d+$/.test(value)) return;
      // Máximo 4 dígitos
      if (value.length > 4) return;
      // No puede empezar con 0
      if (value.length > 1 && value.startsWith('0')) return;
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }
    
    // Validación especial para pesoCalificacion
    if (name === 'pesoCalificacion') {
      // Permitir vacío
      if (value === '' || value === null) {
        setFormData(prev => ({
          ...prev,
          [name]: ''
        }));
        return;
      }
      
      // Convertir a número
      const numValue = parseFloat(value);
      
      // Validar que sea un número válido
      if (isNaN(numValue)) {
        console.warn('⚠️ pesoCalificacion: Solo se permiten números');
        return; // No actualizar si contiene letras
      }
      
      // Validar rango 0-100
      if (numValue < 0) {
        console.warn('⚠️ pesoCalificacion: No se permiten números negativos');
        setFormData(prev => ({
          ...prev,
          [name]: '0'
        }));
        return;
      }
      
      if (numValue > 100) {
        console.warn('⚠️ pesoCalificacion: El valor máximo es 100 (100%)');
        setFormData(prev => ({
          ...prev,
          [name]: '100'
        }));
        return;
      }
      
      // Si es válido, actualizar
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      // Para otros campos, comportamiento normal
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }, []);

  const handleSelectChange = useCallback((e) => {
    const { name, value } = e.target;
    const selectedValue = value ? value.value : '';
    
    setFormData(prev => ({
      ...prev,
      [name]: selectedValue
    }));
  }, []);

  const handleCheckboxChange = useCallback((e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  }, []);

  const handleCargoChange = useCallback((cargoId) => {
    setFormData(prev => ({
      ...prev,
      cargosSeleccionados: prev.cargosSeleccionados.includes(cargoId)
        ? prev.cargosSeleccionados.filter(id => id !== cargoId)
        : [...prev.cargosSeleccionados, cargoId]
    }));
  }, []);

  const handleMarcarTodosCargos = useCallback((cargosDisponibles) => {
    setFormData(prev => ({
      ...prev,
      cargosSeleccionados: prev.cargosSeleccionados.length === cargosDisponibles.length 
        ? []
        : cargosDisponibles.map(c => c.value)
    }));
  }, []);

  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      nombreEvaluacion: '',
      tipoPrueba: '',
      subtipoPruebaPsicologica: '',
      idTipoCargo: '',
      cargosSeleccionados: [],
      encabezadoDescripcion: '',
      instrucciones: '',
      tiempoLimite: '',
      puntajeAprobatorio: 0,
      pesoCalificacion: '',
      estadoActivo: true,
      esObligatoria: true,
      preguntas: [],
      puntajeTotal: 0
    });
  }, []);

  return {
    formData,
    setFormData,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleCargoChange,
    handleMarcarTodosCargos,
    updateFormData,
    resetForm
  };
};

export default useEvaluacionForm;